import { useState, useContext, useEffect, useRef } from 'react';
import { Box, Typography, Button, Fade } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewIcon from '@mui/icons-material/GridView';
import { ThemeContext, LeftPanelContext } from '../Context';

// Pastille numérotée commune aux trois étapes
function StepBadge({ number, isDark }) {
  return (
    <Box sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      background: isDark
        ? 'linear-gradient(135deg, rgb(37, 53, 100) 0%, rgb(52, 74, 141) 100%)'
        : 'linear-gradient(135deg, #1a253c 0%, #2d407a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mr: 2,
      flexShrink: 0,
      boxShadow: isDark
        ? '0 4px 12px rgba(52, 74, 141, 0.4)'
        : '0 4px 12px rgba(26, 37, 60, 0.3)'
    }}>
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
        {number}
      </Typography>
    </Box>
  );
}

/**
 * Écran d'attente affiché quand le PV ne contient encore aucune rubrique.
 * Guide l'utilisateur vers le panneau gauche : encodage puis rubriques/canevas.
 * Props :
 * - title : titre principal ("Commencez à rédiger" / "Document vide")
 * - icon : icône affichée dans le carré animé du haut
 * - step3Text : texte de l'étape 3 ("Modifiez-les ici" / "Visionnez-les ici")
 */
function EmptyReportGuide({ title, icon, step3Text }) {
  const { theme } = useContext(ThemeContext);
  const { activeView, triggerHighlight } = useContext(LeftPanelContext);
  const [arrowVisible, setArrowVisible] = useState(false);
  const arrowTimerRef = useRef(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    return () => {
      if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
    };
  }, []);

  // Ouvre la vue cible dans le panneau gauche et affiche la flèche de guidage
  const handleGuideTo = (view) => {
    triggerHighlight(view);
    setArrowVisible(false);
    if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
    // Ré-affichage dans le tick suivant pour relancer l'animation à chaque clic
    requestAnimationFrame(() => setArrowVisible(true));
    arrowTimerRef.current = setTimeout(() => setArrowVisible(false), 2500);
  };

  const stepCardSx = {
    display: 'flex',
    alignItems: 'center',
    p: 2,
    mb: 1.5,
    borderRadius: '12px',
    width: '100%'
  };

  // Style d'un bouton du guide : blanc par défaut, bleu dégradé pour la vue
  // actuellement ouverte dans le panneau gauche (pour la distinguer)
  const guideButtonSx = (view) => {
    const isSelected = activeView === view;
    const base = {
      textTransform: 'none',
      borderRadius: '10px',
      fontWeight: 600,
      fontSize: '0.9rem',
      px: 2,
      py: 1,
      transition: 'all 0.25s ease'
    };

    if (isSelected) {
      return {
        ...base,
        background: isDark
          ? 'linear-gradient(135deg, rgb(37, 53, 100) 0%, rgb(52, 74, 141) 100%)'
          : 'linear-gradient(135deg, #1a253c 0%, #2d407a 100%)',
        color: '#fff',
        border: '1px solid transparent',
        boxShadow: isDark
          ? '0 4px 14px rgba(52, 74, 141, 0.4)'
          : '0 4px 14px rgba(26, 37, 60, 0.3)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark
            ? '0 6px 20px rgba(52, 74, 141, 0.55)'
            : '0 6px 20px rgba(26, 37, 60, 0.45)',
          background: isDark
            ? 'linear-gradient(135deg, rgb(44, 63, 118) 0%, rgb(62, 88, 166) 100%)'
            : 'linear-gradient(135deg, #22304e 0%, #38509a 100%)'
        }
      };
    }

    return {
      ...base,
      background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff',
      color: isDark ? '#e3ecf7' : '#2d407a',
      border: `1px solid ${isDark ? 'rgba(52, 74, 141, 0.6)' : 'rgba(45, 64, 122, 0.35)'}`,
      boxShadow: isDark
        ? '0 2px 8px rgba(0, 0, 0, 0.25)'
        : '0 2px 8px rgba(26, 37, 60, 0.1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        background: isDark ? 'rgba(52, 74, 141, 0.3)' : '#fff',
        borderColor: isDark ? '#4fc3f7' : '#2d407a',
        boxShadow: isDark
          ? '0 4px 14px rgba(52, 74, 141, 0.35)'
          : '0 4px 14px rgba(26, 37, 60, 0.2)'
      }
    };
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      p: 3,
      backgroundColor: isDark ? 'rgb(38, 53, 65)' : '#f5f7fa',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Quadrillage en arrière-plan avec diffusion */}
      <Box sx={{
        position: 'absolute',
        width: 600,
        height: 600,
        backgroundImage: isDark
          ? `linear-gradient(rgba(79, 195, 247, 0.12) 1px, transparent 1px),
             linear-gradient(90deg, rgba(79, 195, 247, 0.12) 1px, transparent 1px)`
          : `linear-gradient(rgba(45, 64, 122, 0.2) 1px, transparent 1px),
             linear-gradient(90deg, rgba(45, 64, 122, 0.2) 1px, transparent 1px)`,
        backgroundSize: '35px 35px',
        maskImage: 'radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 75%)',
        animation: 'gridPulse 4s ease-in-out infinite',
        '@keyframes gridPulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.8, transform: 'scale(1.02)' }
        }
      }} />

      {/* Flèche animée pointant vers le panneau gauche */}
      <Fade in={arrowVisible} timeout={{ enter: 200, exit: 500 }}>
        <Box sx={{
          position: 'absolute',
          left: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
          animation: arrowVisible ? 'arrowNudge 0.7s ease-in-out infinite' : 'none',
          '@keyframes arrowNudge': {
            '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
            '50%': { transform: 'translateY(-50%) translateX(-10px)' }
          }
        }}>
          <KeyboardDoubleArrowLeftIcon sx={{
            fontSize: 64,
            color: isDark ? '#4fc3f7' : '#2d407a',
            filter: isDark
              ? 'drop-shadow(0 0 12px rgba(79, 195, 247, 0.6))'
              : 'drop-shadow(0 0 12px rgba(45, 64, 122, 0.4))'
          }} />
        </Box>
      </Fade>

      {/* Conteneur principal */}
      <Fade in={true} timeout={600}>
        <Box sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 420,
          width: '100%'
        }}>
          {/* Icône principale animée */}
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '20px',
            background: isDark
              ? 'linear-gradient(135deg, rgb(37, 53, 100) 0%, rgb(52, 74, 141) 100%)'
              : 'linear-gradient(135deg, #1a253c 0%, #2d407a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: isDark
              ? '0 10px 40px rgba(52, 74, 141, 0.4)'
              : '0 10px 40px rgba(26, 37, 60, 0.35)',
            animation: 'iconPulse 3s ease-in-out infinite',
            '@keyframes iconPulse': {
              '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
              '50%': { transform: 'scale(1.05) rotate(2deg)' }
            }
          }}>
            {icon}
          </Box>

          {/* Titre */}
          <Typography
            variant="h5"
            sx={{
              color: isDark ? '#ecf0f1' : '#2c3e50',
              fontWeight: 600,
              textAlign: 'center',
              mb: 4
            }}
          >
            {title}
          </Typography>

          <Box sx={{ width: '100%' }}>
            {/* Étape 1 : bouton vers l'encodage */}
            <Fade in={true} timeout={800}>
              <Box sx={stepCardSx}>
                <StepBadge number={1} isDark={isDark} />
                <Button
                  fullWidth
                  startIcon={<PlaylistAddCheckIcon />}
                  onClick={() => handleGuideTo('elements')}
                  sx={guideButtonSx('elements')}
                >
                  Effectuez vos encodages
                </Button>
              </Box>
            </Fade>

            {/* Étape 2 : deux boutons côte à côte */}
            <Fade in={true} timeout={800} style={{ transitionDelay: '200ms' }}>
              <Box sx={stepCardSx}>
                <StepBadge number={2} isDark={isDark} />
                <Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 0, alignItems: 'center' }}>
                  <Button
                    startIcon={<FormatListBulletedIcon />}
                    onClick={() => handleGuideTo('rubrics')}
                    sx={{ ...guideButtonSx('rubrics'), flex: 1, minWidth: 0, px: 1, fontSize: '0.8rem' }}
                  >
                    Ajoutez des rubriques
                  </Button>
                  <Typography sx={{
                    color: isDark ? '#bdc3c7' : '#5a6a7a',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                    flexShrink: 0
                  }}>
                    ou
                  </Typography>
                  <Button
                    startIcon={<GridViewIcon />}
                    onClick={() => handleGuideTo('standardReports')}
                    sx={{ ...guideButtonSx('standardReports'), flex: 1, minWidth: 0, px: 1, fontSize: '0.8rem' }}
                  >
                    Sélectionnez un canevas
                  </Button>
                </Box>
              </Box>
            </Fade>

            {/* Étape 3 : désigne la zone centrale — encart en pointillés,
                code visuel « emplacement » et non « bouton » */}
            <Fade in={true} timeout={800} style={{ transitionDelay: '400ms' }}>
              <Box sx={{ ...stepCardSx, mb: 0, cursor: 'default' }}>
                <StepBadge number={3} isDark={isDark} />
                <Box sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: '10px',
                  border: `2px dashed ${isDark ? 'rgba(79, 195, 247, 0.35)' : 'rgba(45, 64, 122, 0.3)'}`,
                  backgroundColor: 'transparent'
                }}>
                  <Typography sx={{
                    color: isDark ? '#bdc3c7' : '#5a6a7a',
                    fontWeight: 500,
                    fontStyle: 'italic',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    {step3Text}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
}

export default EmptyReportGuide;
